import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import ListingCard from '../components/ListingCard';
import PremiumBadge from '../components/PremiumBadge';
import ContactOwnerModal from '../components/ContactOwnerModal';
import ShareProfile from '../components/ShareProfile';
import sumInCurrency from '../utils/sumInCurrency';
import { formatMoney } from '../utils/fx';
import { Icon } from '../tokens';
import './Profile.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function PublicProfile() {
  const { id } = useParams();
  const { user: viewer, loading: authLoading, followSeller, unfollowSeller } = useAuth();
  const { addItem, has } = useCart();
  const { displayCurrency, rates } = useCurrency();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [publications, setPublications] = useState([]);
  const [collections, setCollections] = useState([]);
  const [activeColl, setActiveColl] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [contactPub, setContactPub] = useState(null); // publicación para modal de cambio
  const [following, setFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);

  // Visitante sin registro: el perfil se muestra (si su dueño lo tiene
  // público) pero cualquier interacción con el coleccionista o sus piezas lo
  // lleva a /trigger.
  const isAnon = !authLoading && !viewer;
  const goTrigger = () => navigate('/trigger');

  const SCALE_ALL = t('profile.scale_all');
  const SCALE_FILTERS = [SCALE_ALL, '1/18', '1/24', '1/43', '1/64', '1/87'];
  const [scale, setScale] = useState(SCALE_ALL);

  useEffect(() => { setScale(SCALE_ALL); setActiveColl(null); }, [SCALE_ALL, id]);

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;
    setStatus('loading');

    Promise.all([
      axios.get(`${API}/api/users/${id}`),
      axios.get(`${API}/api/publications`, { params: { user: id, limit: 60 } }),
      axios.get(`${API}/api/collections`, { params: { user: id } })
        .then((r) => r.data).catch(() => ({ collections: [] })),
      axios.get(`${API}/api/follows/${id}/status`).then((r) => r.data).catch(() => ({ following: false, followersCount: 0 })),
    ])
      .then(([uRes, pRes, cRes, fRes]) => {
        if (cancelled) return;
        setProfile(uRes.data.user);
        setPublications(pRes.data.publications || []);
        setCollections(cRes.collections || []);
        setFollowing(!!fRes.following);
        setFollowersCount(fRes.followersCount ?? uRes.data.user.followersCount ?? 0);
        setStatus('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        // 403 = perfil privado para un visitante anónimo. 401 = sesión caída.
        if (err.response?.status === 403 || err.response?.status === 401) { navigate('/trigger'); return; }
        setStatus(err.response?.status === 404 ? 'notfound' : 'error');
      });

    return () => { cancelled = true; };
  }, [id, viewer, authLoading, navigate]);

  if (status === 'loading') {
    return (
      <div className="profile">
        <div className="profile-body container">
          <p className="coll-empty">{t('public_profile.loading')}</p>
        </div>
      </div>
    );
  }
  if (status === 'notfound') {
    return (
      <div className="profile">
        <div className="profile-body container">
          <p className="coll-empty">{t('public_profile.not_found')}</p>
        </div>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="profile">
        <div className="profile-body container">
          <p className="coll-empty">{t('public_profile.load_error')}</p>
        </div>
      </div>
    );
  }

  const isOwn = viewer && viewer._id === profile._id;
  const visible = publications.filter((p) => {
    if (activeColl && !(p.collections || []).map(String).includes(String(activeColl))) return false;
    if (scale !== SCALE_ALL && p.scale !== scale) return false;
    return true;
  });
  const totalPieces = publications.length;
  const forSale = publications.filter((p) => p.action === 'Venta').length;
  const forTrade = publications.filter((p) => p.action === 'Cambio').length;

  const totalsByCurrency = Array.isArray(profile.totalsByCurrency) ? profile.totalsByCurrency : [];
  // El valor total de la colección se respeta siempre, con o sin registro: si
  // el coleccionista lo tiene restringido, ni siquiera llega desde el backend.
  const showValue = profile.showTotalValue !== false && totalsByCurrency.length > 0;
  const totalConverted = showValue
    ? sumInCurrency(totalsByCurrency, displayCurrency, rates)
    : 0;
  const stats = [
    {
      label: t('profile.total_value'),
      value: showValue ? formatMoney(totalConverted, displayCurrency, i18n.language) : '••••',
      extra: <span className="stat-sub">{showValue ? t('profile.usd_estimated') : t('public_profile.hidden_by_collector')}</span>,
    },
    { label: t('profile.pieces'),   value: String(totalPieces), extra: <span className="stat-sub">{t('public_profile.in_garage_his')}</span> },
    { label: t('profile.for_sale'), value: String(forSale),     extra: <span className="stat-sub">{t('profile.active')}</span> },
    { label: t('profile.trade'),    value: String(forTrade),    extra: <span className="stat-sub">{t('profile.available')}</span> },
  ];

  const displayName = profile.username || profile.name;
  // Solo sellers y usuarios Premium pueden recibir seguidores; los perfiles
  // del plan gratuito (role 'user') no muestran el botón de seguir.
  const canFollow = !isOwn && (profile.role === 'seller' || profile.role === 'user_premium');

  const toggleFollow = async () => {
    if (isAnon) return goTrigger();
    if (followBusy) return;
    setFollowBusy(true);
    const wasFollowing = following;
    // Optimista: refleja el cambio al instante y revierte si el server falla.
    setFollowing(!wasFollowing);
    setFollowersCount((c) => Math.max(0, c + (wasFollowing ? -1 : 1)));
    try {
      const data = wasFollowing
        ? await unfollowSeller(profile._id)
        : await followSeller(profile._id);
      setFollowing(data.following);
      setFollowersCount(data.followersCount);
    } catch {
      setFollowing(wasFollowing);
      setFollowersCount((c) => Math.max(0, c + (wasFollowing ? 1 : -1)));
      alert(t('follow.error'));
    } finally {
      setFollowBusy(false);
    }
  };

  const openMessages = () => {
    if (isAnon) return goTrigger();
    navigate('/mensajes', { state: { recipient: {
      _id: profile._id, name: profile.name, username: profile.username,
      avatar: profile.avatar, avatarThumb: profile.avatarThumb,
    } } });
  };

  return (
    <div className="profile">
      <Helmet>
        <title>{displayName} — Troly</title>
        <meta name="description" content={t('public_profile.meta_desc', { name: displayName })} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${displayName} — Troly`} />
        <meta property="og:description" content={t('public_profile.meta_desc', { name: displayName })} />
        {profile.avatar && <meta property="og:image" content={profile.avatar} />}
      </Helmet>

      <div className="profile-body container">
        <header className="profile-header fade-up">
          <div className="profile-av-wrap">
            <img
              src={profile.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name || 'U')}&background=45E6C2&color=0A0A0A&size=128`}
              alt={profile.name}
              className="profile-av"
            />
            <span className="profile-online" />
          </div>
          <h1 className="profile-username">
            {displayName}
            {profile.role === 'user_premium' && (
              <span title="Usuario Premium Validado" style={{ display: 'inline-flex', marginLeft: 8 }}>
                <PremiumBadge size={28} />
              </span>
            )}
            {profile.role === 'seller' && (
              <span style={{
                marginLeft: 10, padding: '3px 10px', borderRadius: 20,
                background: 'rgba(20,184,166,.12)', color: 'var(--teal)',
                fontSize: 11, fontWeight: 700, letterSpacing: '.08em', verticalAlign: 'middle',
              }}>{t('profile.seller_badge')}</span>
            )}
          </h1>
          {profile.username && profile.name && profile.username !== profile.name && (
            <p className="profile-realname">{profile.name}</p>
          )}
          {profile.location && <p className="profile-loc">📍 {profile.location}</p>}
          {profile.bio && <p className="profile-realname" style={{ maxWidth: 520, marginTop: 4 }}>{profile.bio}</p>}

          <div className="profile-actions">
            {isOwn ? (
              <>
                <button className="btn-teal" onClick={() => navigate('/profile')}>
                  {t('public_profile.go_to_panel')}
                </button>
                <ShareProfile profileId={profile._id} displayName={displayName} />
              </>
            ) : (
              <>
                {canFollow && (
                  <button
                    type="button"
                    className={following ? 'btn-ghost' : 'btn-teal'}
                    onClick={toggleFollow}
                    disabled={followBusy}
                  >
                    {Icon.userPlus(14)} {following ? t('follow.following') : t('follow.follow')}
                  </button>
                )}
                <button type="button" className="btn-ghost" onClick={openMessages}>
                  {Icon.message(14)} {t('messages.message_action')}
                </button>
                <ShareProfile profileId={profile._id} displayName={displayName} />
                {isAnon ? (
                  <button
                    type="button"
                    className={canFollow ? 'btn-ghost' : 'btn-teal'}
                    onClick={goTrigger}
                  >
                    {Icon.userPlus(14)} {t('public_profile.join_troly')}
                  </button>
                ) : (
                  <Link to="/" className={canFollow ? 'btn-ghost' : 'btn-teal'} style={{ textDecoration: 'none' }}>
                    {Icon.home(14)} {t('public_profile.back_to_catalog')}
                  </Link>
                )}
                {canFollow && followersCount > 0 && (
                  <span className="profile-followers">
                    {t('follow.followers_count', { count: followersCount })}
                  </span>
                )}
              </>
            )}
          </div>
        </header>

        <div className="stats-grid fade-up" style={{ animationDelay: '.08s' }}>
          {stats.map((s) => (
            <div key={s.label} className="stat-card">
              <div className="stat-card__top">
                <span className="stat-card__label">{s.label}</span>
              </div>
              <div className="stat-card__val">{s.value}</div>
              {s.extra}
            </div>
          ))}
        </div>

        {collections.length > 0 && (
          <section className="profile-coll fade-up" style={{ animationDelay: '.12s' }}>
            <div className="coll-folders">
              <span className="coll-folders__label">{t('profile.collections_label')}</span>
              <button
                className={`chip${activeColl === null ? ' chip--active' : ''}`}
                onClick={() => setActiveColl(null)}
              >
                {t('profile.collection_all')}
              </button>
              {collections.map((c) => (
                <button
                  key={c._id}
                  className={`chip${activeColl === c._id ? ' chip--active' : ''}`}
                  onClick={() => setActiveColl(c._id)}
                  title={c.description || c.name}
                >
                  {Icon.folder(12)} {c.name}
                  {c.count > 0 && <span className="chip-count">{c.count}</span>}
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="profile-coll fade-up" style={{ animationDelay: '.14s' }}>
          <div className="coll-header">
            <h2 className="coll-title">{t('public_profile.his_collection')}</h2>
            <div className="coll-filters">
              {SCALE_FILTERS.map((s) => (
                <button
                  key={s}
                  className={`chip${scale === s ? ' chip--active' : ''}`}
                  onClick={() => setScale(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="coll-grid">
            {visible.length === 0 && (
              <p className="coll-empty">
                {publications.length === 0
                  ? t('public_profile.empty')
                  : t('public_profile.empty_scale')}
              </p>
            )}
            {visible.map((p) => {
              const isMultiSeller = profile.role === 'seller' && (p.units || 1) > 1;
              const available = Math.max(0, (p.units || 1) - (p.unitsSold || 0));
              const pAmount = Number(p.price?.amount) || 0;
              const pCurrency = p.price?.currency || 'USD';
              // Cualquier usuario (user o seller) puede vender. Mostrar carrito
              // mientras haya inventario, precio y no sea el propio dueño.
              const canBuy = p.action === 'Venta'
                && pAmount > 0
                && available > 0
                && !isOwn;
              const canTrade = p.action === 'Cambio' && !isOwn;
              const inCart = has(p._id);
              return (
                <div key={p._id} className="pub-cell" style={{ position: 'relative' }}>
                  <ListingCard
                    // El precio lo resuelve el backend: en el perfil público no
                    // se restringe salvo que el coleccionista lo haya ocultado.
                    listing={{ ...p, locked: !!p.locked }}
                    onClick={isAnon ? goTrigger : undefined}
                  />
                  {isMultiSeller && (
                    <div style={{ position: 'absolute', top: 8, left: 8, padding: '3px 8px', borderRadius: 6, background: 'rgba(0,0,0,.7)', color: '#fff', fontSize: 10, fontWeight: 600, letterSpacing: '.06em' }}>
                      {available} / {p.units || 1} disp.
                    </div>
                  )}
                  {canBuy && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        if (isAnon) return goTrigger();
                        if (inCart) { navigate('/cart'); return; }
                        addItem({
                          publicationId: p._id,
                          listingId: p.listing?._id || p.listing || null,
                          name: p.name,
                          image: p.images?.[0] || '',
                          price: pAmount,
                          currency: pCurrency,
                          sellerId: profile._id,
                          sellerName: displayName,
                          units: available,
                        });
                      }}
                      style={{
                        position: 'absolute', bottom: 96, right: 8,
                        padding: '8px 12px', borderRadius: 8, border: 'none',
                        background: inCart ? 'var(--teal-dark, #0e9e82)' : 'var(--teal)',
                        color: '#0A0A0A', fontWeight: 700, fontSize: 12,
                        cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,.18)',
                      }}
                    >
                      🛒 {inCart ? t('listing_card.view_cart') : t('listing_card.add_to_cart')}
                    </button>
                  )}
                  {canTrade && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        if (isAnon) return goTrigger();
                        setContactPub(p);
                      }}
                      style={{
                        position: 'absolute', bottom: 96, right: 8,
                        padding: '8px 12px', borderRadius: 8, border: 'none',
                        background: 'var(--teal)',
                        color: '#0A0A0A', fontWeight: 700, fontSize: 12,
                        cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,.18)',
                      }}
                    >
                      ✉️ {t('listing_card.contact_owner')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <ContactOwnerModal
        publicationId={contactPub?._id || null}
        pieceName={contactPub?.name || ''}
        ownerName={displayName}
        onClose={() => setContactPub(null)}
      />
    </div>
  );
}
